<script lang="ts">
  import { onMount } from 'svelte'
  import { link } from 'svelte-spa-router'
  import { tStore } from '../../i18n'
  import {
    procurementsApi,
    type ProcurementSortField,
    type ProcurementSummary,
  } from '../../api/procurements'
  import PageHeader from '../../components/PageHeader.svelte'
  import ProcurementPager from './dashboard/ProcurementPager.svelte'
  import DeleteConfirmModal from './dashboard/DeleteConfirmModal.svelte'
  import { aiScoreColor, formatDate, formatValue } from '../../composables/format'
  import { useAbortable } from '../../composables/useAbortable'
  import { useToast } from '../../composables/useToast'

  const PAGE_SIZES = [10, 25, 50, 100, 200] as const
  const DEFAULT_PAGE_SIZE = 25

  let procurements = $state<ProcurementSummary[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  let pageSize = $state(DEFAULT_PAGE_SIZE)
  let offset = $state(0)
  let totalCount = $state<number | null>(null)
  let hasMore = $state(false)

  // Default to most-recently-expired first — the newest arrivals in the
  // Archive are the ones a reviewer is most likely to want to check.
  let sortField = $state<ProcurementSortField | undefined>('deadline')
  let sortDir = $state<'asc' | 'desc'>('desc')

  const { start } = useAbortable()
  const toast = useToast()

  onMount(() => {
    void load(start())
  })

  function reload(): void {
    void load(start())
  }

  async function load(signal: AbortSignal) {
    loading = true
    errorMsg = null
    try {
      let page: ProcurementSummary[]
      let count: number
      for (;;) {
        const filters = {
          archived: true as const,
          limit: pageSize,
          offset,
          sort: sortField,
          order: sortField ? sortDir : undefined,
        }
        ;[page, count] = await Promise.all([
          procurementsApi.list(filters, { signal }),
          procurementsApi.count({ archived: true }, { signal }),
        ])
        if (signal.aborted) return
        // A delete (single or bulk) can clear this exact page — most often
        // a bulk delete taking out everything shown — leaving offset
        // pointing past the new end of the list. Land on the tenders'
        // actual last page instead of showing a false "nothing archived"
        // empty state when there's still content elsewhere.
        if (page.length === 0 && offset > 0 && count > 0) {
          offset = Math.max(0, Math.ceil(count / pageSize) - 1) * pageSize
          continue
        }
        break
      }
      procurements = page
      totalCount = count
      hasMore = offset + page.length < count
    } catch (err) {
      if (signal.aborted) return
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  function changePageSize(next: number) {
    if (next === pageSize) return
    pageSize = next
    offset = 0
    reload()
  }

  function prevPage() {
    if (offset === 0) return
    offset = Math.max(0, offset - pageSize)
    reload()
  }

  function nextPage() {
    if (!hasMore) return
    offset = offset + pageSize
    reload()
  }

  function goToPage(page: number) {
    offset = (page - 1) * pageSize
    reload()
  }

  function toggleSort(field: ProcurementSortField) {
    if (sortField === field) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc'
    } else {
      sortField = field
      sortDir = 'desc'
    }
    offset = 0
    reload()
  }

  function sortIndicator(field: ProcurementSortField): string {
    if (sortField !== field) return '↕'
    return sortDir === 'asc' ? '▲' : '▼'
  }

  function ariaSort(field: ProcurementSortField): 'none' | 'ascending' | 'descending' {
    if (sortField !== field) return 'none'
    return sortDir === 'asc' ? 'ascending' : 'descending'
  }

  function sortLabel(field: ProcurementSortField, column: string): string {
    const state =
      sortField !== field
        ? $tStore('views.procurements.sort.none')
        : sortDir === 'asc'
          ? $tStore('views.procurements.sort.ascending')
          : $tStore('views.procurements.sort.descending')
    return `${column} — ${state}`
  }

  let currentPage = $derived(Math.floor(offset / pageSize) + 1)
  let totalPages = $derived(totalCount !== null ? Math.ceil(totalCount / pageSize) : null)
  let rangeFrom = $derived(procurements.length === 0 ? 0 : offset + 1)
  let rangeTo = $derived(offset + procurements.length)

  // --- Row selection (bulk delete) -----------------------------------------

  let bulkMode = $state(false)
  let selectedIds = $state<Set<number>>(new Set())

  function toggleBulkMode() {
    bulkMode = !bulkMode
    if (!bulkMode) selectedIds = new Set()
  }

  function toggleSelect(id: number): boolean {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selectedIds = next
    return next.has(id)
  }

  let headerCheckboxChecked = $derived(
    procurements.length > 0 && procurements.every((p) => selectedIds.has(p.id)),
  )

  // Only ever touches *this page's* ids within the existing set — selection
  // persists across pages (see the removed reset in load()), so replacing
  // selectedIds outright here would silently drop every other page's picks
  // the moment "select all" is used on a second page.
  function toggleSelectAll(): boolean {
    const next = new Set(selectedIds)
    if (headerCheckboxChecked) {
      for (const p of procurements) next.delete(p.id)
      selectedIds = next
      return false
    }
    for (const p of procurements) next.add(p.id)
    selectedIds = next
    return true
  }

  // --- Delete (single row or bulk selection) -------------------------------

  type DeleteTarget = { id: number; title: string } | { count: number }

  let deleteTarget = $state<DeleteTarget | null>(null)
  let deleteInFlight = $state(false)

  function confirmDelete(p: ProcurementSummary) {
    deleteTarget = { id: p.id, title: p.title }
  }

  function confirmBulkDelete() {
    if (selectedIds.size === 0) return
    deleteTarget = { count: selectedIds.size }
  }

  function cancelDelete() {
    if (deleteInFlight) return
    deleteTarget = null
  }

  async function executeDeletion() {
    if (!deleteTarget || deleteInFlight) return
    deleteInFlight = true
    try {
      if ('id' in deleteTarget) {
        await deleteOne(deleteTarget.id)
      } else {
        await deleteSelected()
      }
      deleteTarget = null
    } finally {
      deleteInFlight = false
    }
  }

  async function deleteOne(id: number) {
    try {
      await procurementsApi.delete(id)
      toast.show($tStore('views.dashboard.deleteSuccess'), 'success')
      // Reload rather than splice the row out locally — deleting the last
      // tender on this page must not strand the view on a now-empty page
      // while the rest of the archive still has content (see load()'s
      // clamp-back-to-the-last-page logic).
      reload()
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : $tStore('views.dashboard.deleteError'),
        'error',
      )
    }
  }

  async function deleteSelected() {
    const ids = [...selectedIds]
    try {
      const result = await procurementsApi.bulkDelete(ids)
      const deletedCount = result.results.filter((r) => r.status === 'deleted').length
      selectedIds = new Set()
      bulkMode = false
      toast.show($tStore('views.archive.bulkDeleteSuccess', { count: deletedCount }), 'success')
      // Reload rather than splice rows out locally — a bulk delete very
      // commonly clears the whole page (that's the point of selecting
      // everything on it), which must not strand the view on a now-empty
      // page while the rest of the archive still has content.
      reload()
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : $tStore('views.archive.bulkDeleteError'),
        'error',
      )
    }
  }
</script>

<section class="archive">
  {#if !bulkMode}
    <!-- Idle state: title + subtitle + "Select tenders" stay one combined
         row (PageHeader's own actions slot), wrapped so that whole row
         sticks and scrolls as a single unit — not the title scrolling away
         while the button stays behind on its own. Hidden entirely (not just
         its button) once bulk mode is active — it must not stack on top of
         the bulk-bar below, which takes over the same sticky slot. -->
    <div class="archive-header">
      <PageHeader
        title={$tStore('views.archive.title')}
        subtitle={$tStore('views.archive.subtitle')}
      >
        {#snippet actions()}
          <button type="button" class="btn btn--secondary" onclick={toggleBulkMode}>
            {$tStore('views.archive.selectMode')}
          </button>
        {/snippet}
      </PageHeader>
    </div>
  {/if}

  {#if bulkMode}
    <!-- Sticks just below the app header, same as the idle row above — the
         active selection toolbar. Selection survives paging/sorting (cleared
         only on explicit exit or a successful delete), so this keeps an
         accurate count across those while active. -->
    <div class="bulk-bar">
      <span class="bulk-bar__count">
        {$tStore('views.archive.selectedCount', { count: selectedIds.size })}
      </span>
      <button type="button" class="btn btn--secondary btn--sm" onclick={toggleBulkMode}>
        {$tStore('views.archive.exitSelectMode')}
      </button>
      <button
        type="button"
        class="btn btn--danger btn--sm"
        onclick={confirmBulkDelete}
        disabled={selectedIds.size === 0}
        title={$tStore('views.archive.deleteSelectedTooltip')}
      >
        {$tStore('views.archive.deleteSelected')}{selectedIds.size > 0
          ? ` (${selectedIds.size})`
          : ''}
      </button>
    </div>
  {/if}

  {#if loading && procurements.length === 0}
    <p class="muted">{$tStore('views.procurements.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if procurements.length === 0}
    <p class="muted">{$tStore('views.archive.empty')}</p>
  {:else}
    <div class="card table-wrap" class:table-wrap--busy={loading} aria-busy={loading}>
      <table class="table">
        <thead>
          <tr>
            {#if bulkMode}
              <th class="select-col">
                <input
                  type="checkbox"
                  checked={headerCheckboxChecked}
                  onchange={(e) => {
                    const target = e.currentTarget
                    target.checked = toggleSelectAll()
                  }}
                  aria-label={$tStore('views.dashboard.selectAllOnPage')}
                />
              </th>
            {/if}
            <th>{$tStore('views.archive.columns.view')}</th>
            <th aria-sort={ariaSort('value')}>
              <button
                type="button"
                class="sort-btn"
                class:sort-btn--active={sortField === 'value'}
                onclick={() => toggleSort('value')}
                aria-label={sortLabel('value', $tStore('views.procurements.columns.value'))}
              >
                {$tStore('views.procurements.columns.value')}
                <span class="sort-ind" aria-hidden="true">{sortIndicator('value')}</span>
              </button>
            </th>
            <th class="score-col" aria-sort={ariaSort('aiScore')}>
              <button
                type="button"
                class="sort-btn"
                class:sort-btn--active={sortField === 'aiScore'}
                onclick={() => toggleSort('aiScore')}
                aria-label={sortLabel('aiScore', $tStore('views.procurements.columns.aiScore'))}
              >
                {$tStore('views.procurements.columns.aiScore')}
                <span class="sort-ind" aria-hidden="true">{sortIndicator('aiScore')}</span>
              </button>
            </th>
            <th class="date-cell" aria-sort={ariaSort('deadline')}>
              <button
                type="button"
                class="sort-btn"
                class:sort-btn--active={sortField === 'deadline'}
                onclick={() => toggleSort('deadline')}
                aria-label={sortLabel('deadline', $tStore('views.procurements.columns.deadlineAt'))}
              >
                {$tStore('views.procurements.columns.deadlineAt')}
                <span class="sort-ind" aria-hidden="true">{sortIndicator('deadline')}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {#each procurements as p (p.id)}
            <tr aria-selected={bulkMode ? selectedIds.has(p.id) : undefined}>
              {#if bulkMode}
                <td class="select-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onchange={(e) => {
                      const target = e.currentTarget
                      target.checked = toggleSelect(p.id)
                    }}
                    aria-label={p.title}
                  />
                </td>
              {/if}
              <td>
                <a href={`/procurements/${p.id}`} use:link class="title-link">
                  {p.title}
                </a>
                <div class="row-meta">
                  <button
                    type="button"
                    class="btn btn--danger btn--sm delete-btn"
                    onclick={() => confirmDelete(p)}
                    title={$tStore('views.dashboard.deleteProcurementTooltip')}
                    aria-label={$tStore('views.dashboard.deleteProcurementTooltip')}
                  >
                    {$tStore('views.dashboard.deleteProcurement')}
                  </button>
                </div>
              </td>
              <td>{formatValue(p.valueEur, p.valueOriginal, p.currency)}</td>
              <td class="score-cell">
                {#if p.aiScore !== null}
                  <a
                    href={`/procurements/${p.id}?focus=ai-score`}
                    use:link
                    class="score-link"
                    style:color={aiScoreColor(p.aiScore)}
                    title={$tStore('views.dashboard.viewAiScore')}
                  >
                    {p.aiScore}%
                  </a>
                {:else}
                  —
                {/if}
              </td>
              <td class="date-cell">{formatDate(p.deadlineAt)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <ProcurementPager
      {pageSize}
      {offset}
      {rangeFrom}
      {rangeTo}
      {currentPage}
      {totalPages}
      {totalCount}
      {hasMore}
      {loading}
      pageSizes={PAGE_SIZES}
      onPageSizeChange={changePageSize}
      onPrev={prevPage}
      onNext={nextPage}
      onGoToPage={goToPage}
    />
  {/if}
</section>

<DeleteConfirmModal
  target={deleteTarget}
  inFlight={deleteInFlight}
  onConfirm={executeDeletion}
  onCancel={cancelDelete}
/>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .archive {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  // Wraps the idle-state PageHeader (title + subtitle + "Select tenders") so
  // the whole row sticks and scrolls together as one unit, directly under
  // the app header (same position: sticky + top/z-index pattern
  // AppHeader.svelte uses for itself). An opaque background is required —
  // sticky content still needs to occlude whatever scrolls underneath it.
  .archive-header {
    position: sticky;
    // Flush against the app header — no gap in the *box's* own position, so
    // there's no uncovered strip for content to flash through while
    // scrolling. The visual breathing room below comes from padding-top
    // instead: the opaque background still starts right at the header's
    // edge, it just doesn't paint any content there.
    top: v.$header-height;
    z-index: 9;
    padding-top: v.$space-md;
    background: v.$color-bg;
  }

  // Sticks in the same spot once bulk selection is active — the entry
  // button above is gone (PageHeader's actions render nothing), and this
  // toolbar takes over the reachable-while-scrolling role instead.
  .bulk-bar {
    position: sticky;
    top: v.$header-height;
    z-index: 9;
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    padding: v.$space-sm v.$space-md;
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    box-shadow: v.$shadow-sm;
  }

  .bulk-bar__count {
    font-size: 0.875rem;
    font-weight: 600;
    color: v.$color-text;
    margin-right: auto;
  }

  .table-wrap {
    overflow-x: auto;
    padding: 0;
    transition: opacity 0.15s ease;
  }

  .table-wrap--busy {
    opacity: 0.55;
  }

  .title-link {
    font-weight: 600;
    color: v.$color-text;

    &:hover {
      color: v.$color-primary;
      text-decoration: none;
    }
  }

  .row-meta {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    margin-top: v.$space-xs;
  }

  .delete-btn {
    font-size: 0.75rem;
    padding: 0.15rem v.$space-sm;
    background: v.$color-danger-fg;
    color: #fff;
    border-color: v.$color-danger-fg;

    &:hover:not(:disabled) {
      background: #991b1b;
      border-color: #991b1b;
    }
  }

  .select-col {
    width: 1%;
    white-space: nowrap;
    text-align: center;
  }

  .score-col {
    text-align: center;
  }

  .score-cell {
    font-weight: 600;
    color: v.$color-text;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .score-link {
    font-weight: inherit;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .date-cell {
    white-space: nowrap;
  }

  .sort-btn {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;

    &:hover {
      color: v.$color-primary;
    }

    &--active {
      color: v.$color-primary;
    }
  }

  .sort-ind {
    font-size: 0.7rem;
    line-height: 1;
    opacity: 0.7;
  }

  .sort-btn--active .sort-ind {
    opacity: 1;
  }
</style>
